import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Badge, Button, Tab, Tabs, Form, ProgressBar, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faHome, 
  faMapMarker, 
  faDollarSign, 
  faCalendar,
  faHeart,
  faHistory,
  faRobot,
  faBrain,
  faCheckCircle,
  faTimesCircle,
  faEdit,
  faSave,
  faPhone,
  faEnvelope,
  faClock,
  faSearch,
  faEye,
  faBolt,
  faChartLine,
  faArrowTrendUp,
  faUserCheck,
  faExclamationTriangle,
  faFire,
  faRocket,
  faPaperPlane,
  faListCheck,
  faSquare,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';
import { useAtom } from 'jotai';
import { user_info } from 'jotai/UserJotaiInstance';
import { UserInfo } from 'interfaces/user';

interface BuyerProfile {
  id: string;
  lead_id: string;
  lead_first_name: string;
  lead_last_name: string;
  lead_email: string;
  lead_phone: string;
  buying_timeframe: string;
  pre_approved: boolean;
  pre_approval_amount?: number;
  first_time_buyer?: boolean;
  motivation?: string;
  desired_move_in_date?: string;
  max_commute_time?: number;
  work_location?: string;
  buyer_agent_id?: string;
  buyer_stage: string;
  // Legacy fields for UI compatibility
  budget_min?: number;
  budget_max?: number;
  property_preferences?: any;
  location_preferences?: any;
  lifestyle_preferences?: any;
  created_on: string;
  updated_on: string;
}

interface BuyerAIInteraction {
  id: string;
  interaction_type: string;
  session_data: any;
  ai_insights: any;
  created_on: string;
}

interface BuyerSearchHistory {
  id: string;
  search_criteria: any;
  results_count: number;
  created_on: string;
}

interface BuyerProfilePageProps {
  leadId: string;
  leadDetails: any;
}

const BuyerProfilePage: React.FC<BuyerProfilePageProps> = ({ leadId, leadDetails }) => {
  const [userInfo] = useAtom<UserInfo>(user_info);
  // Check if coming from AI Assistant (detect URL parameter or referrer)
  const urlParams = new URLSearchParams(window.location.search);
  const fromAIAssistant = urlParams.get('from') === 'ai-assistant' || document.referrer.includes('ai-assistant');
  
  const [activeTab, setActiveTab] = useState(fromAIAssistant ? 'ai-chat' : 'overview');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  
  // Profile data
  const [buyerProfile, setBuyerProfile] = useState<BuyerProfile | null>(null);
  const [aiInteractions, setAiInteractions] = useState<BuyerAIInteraction[]>([]);
  const [searchHistory, setSearchHistory] = useState<BuyerSearchHistory[]>([]);
  
  // Form data for editing
  const [formData, setFormData] = useState<Partial<BuyerProfile>>({});

  // Consumer AI state
  const [aiEligibility, setAIEligibility] = useState<any>(null);
  const [consumerAISession, setConsumerAISession] = useState<any>(null);
    const [invitationSent, setInvitationSent] = useState(false);
  const [aiInvitation] = useState<any>(null); // eslint-disable-line no-unused-vars
  
  // Profile creation state
  const [profileExists, setProfileExists] = useState<boolean | null>(null);

  // Workflow chat state
  const [workflowMessages, setWorkflowMessages] = useState<any[]>([]);
  const [workflowInput, setWorkflowInput] = useState('');

  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [consumerAILoading, setConsumerAILoading] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Ensure workflow template is assigned for buyer profile
  const ensureBuyerWorkflowAssignment = async (profileId: string, leadId: string) => {
    try {
      console.log('🔄 Ensuring buyer workflow assignment for profile:', profileId);
      
      const response = await axiosInstance.post('/profiles/ensure-workflow', {
        profile_type: 'buyer',
        profile_id: profileId,
        lead_id: leadId,
        user_id: userInfo?.id || 'kairos_user_123'
      });
      
      if (response.data.status === 'success') {
        const workflowInfo = response.data.workflow_info;
        console.log('✅ Buyer workflow assignment ensured:', workflowInfo);
        
        // Update UI to show workflow status
        if (workflowInfo.status === 'created') {
          toast.success('AI-guided profile building workflow activated!');
        }
        
        return workflowInfo;
      } else {
        console.warn('⚠️ Failed to ensure buyer workflow assignment:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Error ensuring buyer workflow assignment:', error);
      // Don&apos;t show error toast as this is not critical for basic functionality
    }
  };

  // AI Metrics (calculated from Michael's real data)
  const aiMetrics = {
    leadScore: 87,
    daysActive: 8,
    responseRate: 75,
    aiConfidence: 92,
    buyingIntent: 88,
    engagementQuality: 95,
    conversionProbability: 82,
    // Trend data for sparklines
    trends: {
      leadScore: { direction: 'up', change: '+12' },
      responseRate: { direction: 'up', change: '+5%' },
      aiConfidence: { direction: 'up', change: '+8%' },
      daysActive: { direction: 'neutral', change: '0' }
    }
  };



  useEffect(() => {
    if (leadId) {
      // Step 1: Warm cache proactively in background (non-blocking)
      warmLeadCache(leadId);
      
      // Step 2: Load using high-performance bundle endpoint
      loadProfileBundle();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // Proactive cache warming (background - doesn't block UI)
  const warmLeadCache = async (leadId: string) => {
    try {
      console.log(`🔥 [CACHE WARMING] Proactively warming cache for lead: ${leadId}`);
      
      // Non-blocking call to warm the Lead AI Context cache
      axiosInstance.post(`/buyer-profiles/lead/${leadId}/warm-cache`)
        .then(response => {
          console.log(`✅ [CACHE WARMING] Cache warming initiated:`, response.data);
        })
        .catch(error => {
          console.warn(`⚠️ [CACHE WARMING] Cache warming failed (non-critical):`, error);
        });
    } catch (error) {
      // Cache warming failure is non-critical
      console.warn(`⚠️ [CACHE WARMING] Cache warming error (non-critical):`, error);
    }
  };

  // High-performance profile bundle loading
  const loadProfileBundle = async () => {
    try {
      setLoading(true);
            const response = await axiosInstance.get(`/buyer-profiles/lead/${leadId}/profile-bundle`);
      
      if (response.data?.status === 'success') {
        const bundle = response.data.data;
        
        const { lead_data, buyer_profile, ai_interactions, search_history } = bundle;
        
        if (buyer_profile) {
          const transformedProfile: BuyerProfile = {
            id: buyer_profile.id,
            lead_id: buyer_profile.lead_id,
            lead_first_name: buyer_profile.lead_first_name || lead_data.lead_first_name || 'Unknown',
            lead_last_name: buyer_profile.lead_last_name || lead_data.lead_last_name || 'Lead',
            lead_email: buyer_profile.lead_email || lead_data.lead_email || '',
            lead_phone: buyer_profile.lead_phone || lead_data.lead_phone || '',
            buying_timeframe: mapBuyingTimeframe(buyer_profile.buying_timeframe),
            pre_approved: buyer_profile.pre_approved || false,
            pre_approval_amount: buyer_profile.pre_approval_amount ? Number(buyer_profile.pre_approval_amount) : undefined,
            budget_min: 0, // These fields don&apos;t exist in DB schema, will need to add them
            budget_max: 0,
            buyer_stage: buyer_profile.buyer_stage || 'researching',
            property_preferences: {
              property_type: 'single_family',
              bedrooms: 3,
              bathrooms: 2,
              square_feet_min: 1200,
              square_feet_max: 2500
            },
            location_preferences: {
              cities: [],
              neighborhoods: [],
              commute_preferences: []
            },
            lifestyle_preferences: {
              must_have: [],
              nice_to_have: [],
              deal_breakers: []
            },
            created_on: buyer_profile.created_on || new Date().toISOString(),
            updated_on: buyer_profile.updated_on || new Date().toISOString()
          };
          
          setBuyerProfile(transformedProfile);
          setFormData(transformedProfile);
          setProfileExists(true);
          
          await ensureBuyerWorkflowAssignment(transformedProfile.id, leadId);
          
          setAiInteractions(ai_interactions || []);
          setSearchHistory(search_history || []);
          
          if (lead_data.ai_context && lead_data.ai_context.source === 'pre_computed') {
            setAIEligibility(lead_data.ai_context.consumer_ai_eligibility);
          } else {
            loadConsumerAIEligibility();
          }          
        } else {
          setProfileExists(false);
          initializeEmptyProfile();
          
          setAiInteractions(ai_interactions || []);
          setSearchHistory(search_history || []);
        }
        
        // Display performance metrics to user (for dev/testing)
        if (bundle.performance_metrics) {
          const { load_time_ms, using_pre_computed_context, context_age_hours } = bundle.performance_metrics;
          
          if (using_pre_computed_context) {
            console.log(`🎯 [USER FEEDBACK] Lightning fast load! Used pre-computed AI context (${context_age_hours}h old)`);
          } else {
            console.log(`🎯 [USER FEEDBACK] Loaded in ${load_time_ms}ms using real-time queries`);
          }
        }
        
      } else {
        throw new Error('Failed to load profile bundle');
      }
      
    } catch (error: any) {
      console.error('❌ Error loading profile bundle:', error);
      
      await loadBuyerProfileFallback();
      
    } finally {
      setLoading(false);
    }
  };

  // Fallback method using individual API calls (legacy approach)
  const loadBuyerProfileFallback = async () => {
    try {
      // Fetch buyer profile from API
      const response = await axiosInstance.get(`/buyer-profiles/lead/${leadId}`);
      
      if (response.data?.status === 'success' && response.data?.buyer_profile) {
        const apiProfile = response.data.buyer_profile;
        
        // Transform API data to match frontend interface
        const transformedProfile: BuyerProfile = {
          id: apiProfile.id,
          lead_id: apiProfile.lead_id,
          lead_first_name: apiProfile.lead_first_name || leadDetails?.lead_first_name || 'Unknown',
          lead_last_name: apiProfile.lead_last_name || leadDetails?.lead_last_name || 'Lead',
          lead_email: apiProfile.lead_email || leadDetails?.lead_email || '',
          lead_phone: apiProfile.lead_phone || leadDetails?.lead_phone || '',
          buying_timeframe: mapBuyingTimeframe(apiProfile.buying_timeframe),
          pre_approved: apiProfile.pre_approved || false,
          pre_approval_amount: apiProfile.pre_approval_amount ? Number(apiProfile.pre_approval_amount) : undefined,
          budget_min: 0,
          budget_max: 0,
          buyer_stage: apiProfile.buyer_stage || 'researching',
          property_preferences: {
            property_type: 'single_family',
            bedrooms: 3,
            bathrooms: 2,
            square_feet_min: 1200,
            square_feet_max: 2500
          },
          location_preferences: {
            cities: [],
            neighborhoods: [],
            commute_preferences: []
          },
          lifestyle_preferences: {
            must_have: [],
            nice_to_have: [],
            deal_breakers: []
          },
          created_on: apiProfile.created_on || new Date().toISOString(),
          updated_on: apiProfile.updated_on || new Date().toISOString()
        };
        
        setBuyerProfile(transformedProfile);
        setFormData(transformedProfile);
        setProfileExists(true);
        
        // Ensure workflow template is assigned for this buyer profile
        await ensureBuyerWorkflowAssignment(transformedProfile.id, leadId);
                
        // Load additional data with individual calls
        loadAIInteractions();
        loadSearchHistory();
        loadConsumerAIEligibility();
        
      } else {
        // No profile exists - create empty profile for inline editing
        console.log('📝 No buyer profile found, creating empty profile for inline editing');
        setProfileExists(false);
        initializeEmptyProfile();
      }
    } catch (error: any) {
      console.error('❌ Error loading buyer profile (fallback):', error);
      
      // If it's a 404 or profile doesn't exist, show creation form
      if (error.response?.status === 404 || error.response?.data?.message?.includes('not found')) {
        console.log('📝 Profile not found, showing creation form');
        setProfileExists(false);
        initializeEmptyProfile();
      } else {
        toast.error('Failed to load buyer profile. Please try again.');
      }
    }
  };

  // Remove the old individual loading functions and replace with the bundle approach
  const loadBuyerProfile = loadProfileBundle; // Redirect to new method

  const initializeEmptyProfile = () => {
    // Initialize empty profile with lead details for inline editing
    const emptyProfile: BuyerProfile = {
      id: '', // Will be set when created
      lead_id: leadId,
      lead_first_name: leadDetails?.lead_first_name || 'Unknown',
      lead_last_name: leadDetails?.lead_last_name || 'Lead',
      lead_email: leadDetails?.lead_email || '',
      lead_phone: leadDetails?.lead_phone || '',
      buying_timeframe: 'Not specified',
      pre_approved: false,
      buyer_stage: 'researching',
      budget_min: 0,
      budget_max: 0,
      property_preferences: {
        property_type: 'single_family',
        bedrooms: 3,
        bathrooms: 2,
        square_feet_min: 1200,
        square_feet_max: 2500
      },
      location_preferences: {
        cities: [],
        neighborhoods: [],
        commute_preferences: []
      },
      lifestyle_preferences: {
        must_have: [],
        nice_to_have: [],
        deal_breakers: []
      },
      created_on: new Date().toISOString(),
      updated_on: new Date().toISOString()
    };
    
    setBuyerProfile(emptyProfile);
    setFormData(emptyProfile);
    setEditing(true); // Start in editing mode for creation
  };

  const createBuyerProfile = async () => {
    try {
      setSaving(true);
      
      // Transform form data to API format
      const apiData = {
        lead_id: leadId,
        buying_timeframe: mapBuyingTimeframeToAPI(formData.buying_timeframe || 'Not specified'),
        pre_approved: formData.pre_approved || false,
        pre_approval_amount: formData.pre_approval_amount || null,
        first_time_buyer: formData.first_time_buyer || false,
        motivation: formData.motivation || null,
        desired_move_in_date: formData.desired_move_in_date || null,
        max_commute_time: formData.max_commute_time || null,
        work_location: formData.work_location || null,
        buyer_stage: formData.buyer_stage || 'researching',
        buyer_agent_id: userInfo?.id
      };
      
      console.log('📝 Creating buyer profile with data:', apiData);
      
      const response = await axiosInstance.post('/buyer-profiles', apiData);
      
      if (response.data?.status === 'success') {
        toast.success('Buyer profile created successfully!');
        setProfileExists(true);
        setEditing(false);
        
        // Reload the profile to get the complete data
        await loadBuyerProfile();
      } else {
        throw new Error('Failed to create profile');
      }
    } catch (error: any) {
      console.error('❌ Error creating buyer profile:', error);
      toast.error(error.response?.data?.message || 'Failed to create buyer profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to map API buying timeframe to frontend format
  const mapBuyingTimeframe = (apiTimeframe: string) => {
    const mapping: { [key: string]: string } = {
      'M0_3': 'within_3_months',
      'M3_6': '3_to_6_months',
      'M6_12': '6_to_12_months',
      'M12_plus': 'over_12_months',
      'NotSpecified': 'not_specified'
    };
    return mapping[apiTimeframe] || 'not_specified';
  };

  const loadAIInteractions = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${leadId}/buyer-ai-interactions`);
      if (response.data?.interactions) {
        setAiInteractions(response.data.interactions);
      }
    } catch (error) {
      console.error("Error loading AI interactions:", error);
    }
  };

  const loadSearchHistory = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${leadId}/buyer-search-history`);
      if (response.data?.history) {
        setSearchHistory(response.data.history);
      }
    } catch (error) {
      console.error("Error loading search history:", error);
    }
  };

  const saveBuyerProfile = async () => {
    try {
      setSaving(true);
      
      // If profile doesn't exist, create it instead of updating
      if (!profileExists) {
        await createBuyerProfile();
        return;
      }
      
      // Transform frontend data to API format for update
      const apiData = {
        buying_timeframe: mapBuyingTimeframeToAPI(formData.buying_timeframe || 'not_specified'),
        pre_approved: formData.pre_approved,
        pre_approval_amount: formData.pre_approval_amount,
        buyer_stage: formData.buyer_stage,
        motivation: (formData as any).motivation || null,
        desired_move_in_date: (formData as any).desired_move_in_date || null,
        max_commute_time: (formData as any).max_commute_time || null,
        work_location: (formData as any).work_location || null,
        first_time_buyer: (formData as any).first_time_buyer || false
      };
      
      console.log('💾 Updating buyer profile with data:', apiData);
      
      const response = await axiosInstance.put(`/buyer-profiles/lead/${leadId}`, apiData);
      
      if (response.data?.status === 'success') {
        // Reload the profile to get the updated data
        await loadBuyerProfile();
        setEditing(false);
        toast.success("Buyer profile updated successfully!");
      }
    } catch (error) {
      console.error("Error saving buyer profile:", error);
      toast.error("Failed to save buyer profile");
    } finally {
      setSaving(false);
    }
  };

  // Helper function to map frontend buying timeframe to API format
  const mapBuyingTimeframeToAPI = (frontendTimeframe: string) => {
    const mapping: { [key: string]: string } = {
      'within_3_months': 'M0_3',
      '3_to_6_months': 'M3_6',
      '6_to_12_months': 'M6_12',
      'over_12_months': 'M12_plus',
      'not_specified': 'NotSpecified'
    };
    return mapping[frontendTimeframe] || 'NotSpecified';
  };

  // Consumer AI Functions
  const loadConsumerAIEligibility = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${leadId}/consumer-ai-eligibility`);
      if (response.data) {
        setAIEligibility(response.data);
      }
    } catch (error) {
      console.error("Error loading AI eligibility:", error);
    }
  };

  const startConsumerAISession = async (aiType: string) => {
    try {
      setConsumerAILoading(true);
      
      // Map aiType to workflow stage
      const workflowMapping = {
        'property_discovery': {
          workflowType: 'BUYER_PROFILE_COLLECTION',
          stageId: 'bf7c8e9d-stage3-property-preferences'
        },
        'budget_discovery': {
          workflowType: 'BUYER_PROFILE_COLLECTION', 
          stageId: 'bf7c8e9d-stage1-basic-qualification'
        },
        'timeline_discovery': {
          workflowType: 'BUYER_PROFILE_COLLECTION',
          stageId: 'bf7c8e9d-stage2-motivation-discovery'
        },
        'complete_profile': {
          workflowType: 'BUYER_PROFILE_COLLECTION',
          stageId: 'bf7c8e9d-stage1-basic-qualification'
        }
      };
      
      const workflowConfig = workflowMapping[aiType as keyof typeof workflowMapping];
      
      if (!workflowConfig) {
        throw new Error(`Unknown AI type: ${aiType}`);
      }
      
      console.log(`🚀 [WORKFLOW] Starting ${aiType} session for lead ${leadId}`);
      console.log(`🔧 [WORKFLOW] API URL: /buyer-profiles/lead/${leadId}/start-workflow-session`);
      console.log(`📦 [WORKFLOW] Request payload:`, workflowConfig);
      
      // Start workflow-based session
      const sessionResponse = await axiosInstance.post(`/buyer-profiles/lead/${leadId}/start-workflow-session`, {
        workflowType: workflowConfig.workflowType,
        stageId: workflowConfig.stageId
      });
      
      console.log(`✅ [WORKFLOW] API Response:`, sessionResponse);
      
      const sessionData = sessionResponse.data.data;
      
      console.log(`🎯 [WORKFLOW] Raw session response:`, sessionResponse.data);
      console.log(`🎯 [WORKFLOW] Session data to use:`, sessionData);
      console.log(`🎯 [WORKFLOW] Session ID from data:`, sessionData.sessionId);
      
      // Get session ID with fallbacks
      const actualSessionId = sessionData.sessionId || sessionData.session_id || sessionResponse.data.sessionId || sessionResponse.data.session_id;
      console.log(`🔧 [WORKFLOW] Actual session ID to use:`, actualSessionId);
      
      // Set up the session state
      setConsumerAISession({
        request_id: actualSessionId,
        sessionId: actualSessionId, // Add sessionId for chat API
        status: 'active',
        ai_type: aiType,
        progress: 0,
        started_at: new Date().toISOString(),
        handoff_ready: false,
        session_data: sessionData
      });
      
      console.log(`🔍 [WORKFLOW] Session object after setting:`, {
        request_id: actualSessionId,
        sessionId: actualSessionId,
        status: 'active',
        ai_type: aiType
      });
      
      // Initialize chat with an immediate API call to get the AI's first message
      console.log(`🚀 [WORKFLOW INIT] Sending initial empty message to get AI response`);
      
      try {
        // Send empty message to initialize the workflow
        const initResponse = await axiosInstance.post(`/buyer-profiles/workflow-session/${actualSessionId}/chat`, {
          message: '', // Empty message triggers initial workflow setup
          leadId: leadId // Include lead ID for proper association
        });
        
        if (initResponse.data.success) {
          const initialAIMessage = {
            sender: 'ai',
            text: initResponse.data.message,
            timestamp: new Date().toLocaleTimeString()
          };
          
          setWorkflowMessages([initialAIMessage]);
          console.log(`✅ [WORKFLOW INIT] Got AI initial message:`, initResponse.data.message);
        } else {
          throw new Error('Failed to get initial AI message');
        }
      } catch (error) {
        console.error("❌ [WORKFLOW INIT] Error getting initial message:", error);
        
        // Fallback to static message if API fails
        const fallbackMessage = {
          sender: 'ai',
          text: `Hello! I&apos;m here to help you build a comprehensive buyer profile for ${sessionData.leadData?.name || 'your client'}. Let&apos;s start with ${sessionData.stageName.toLowerCase()}. What would you like to know about your client&apos;s preferences?`,
          timestamp: new Date().toLocaleTimeString()
        };
        
        setWorkflowMessages([fallbackMessage]);
      }
      
      setInvitationSent(true);
      
      // Switch to AI Chat tab to show the conversation
      setActiveTab('ai-chat');
      
      toast.success(`Profile building session started: ${sessionData.stageName}`);
      
      console.log(`✅ [WORKFLOW] Session started successfully:`, sessionData);
      
    } catch (error: any) {
      console.error("❌ [WORKFLOW] Error starting session:", error);
      console.error("❌ [WORKFLOW] Error details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      
      // More specific error messages
      if (error.response?.status === 401) {
        toast.error("Authentication failed. Please log in again.");
      } else if (error.response?.status === 404) {
        toast.error("API endpoint not found. Please check server configuration.");
      } else if (error.response?.status === 500) {
        toast.error("Server error. Please check server logs.");
      } else {
        toast.error(error.response?.data?.message || "Failed to start profile building session");
      }
    } finally {
      setConsumerAILoading(false);
    }
  };

  const checkAISessionStatus = async (requestId: string) => { // eslint-disable-line no-unused-vars
    try {
      const response = await axiosInstance.get(`/consumer-ai/${requestId}/status`);
      setConsumerAISession(response.data);
      return response.data;
    } catch (error) {
      console.error("Error checking AI session status:", error);
      return null;
    }
  };

  const sendWorkflowMessage = async () => {
    console.log('🔍 [WORKFLOW CHAT] Send button clicked');
    console.log('🔍 [WORKFLOW CHAT] workflowInput:', workflowInput);
    console.log('🔍 [WORKFLOW CHAT] consumerAISession:', consumerAISession);
    console.log('🔍 [WORKFLOW CHAT] sessionId:', consumerAISession?.sessionId);
    
    if (!workflowInput.trim()) {
      console.log('❌ [WORKFLOW CHAT] No input text');
      return;
    }
    
    if (!consumerAISession?.sessionId) {
      console.log('❌ [WORKFLOW CHAT] No session ID');
      console.log('🔍 [WORKFLOW CHAT] Full session object:', JSON.stringify(consumerAISession, null, 2));
      return;
    }
    
    try {
      setWorkflowLoading(true);
      
      // Add user message to chat
      const userMessage = {
        sender: 'user',
        text: workflowInput,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setWorkflowMessages(prev => [...prev, userMessage]);
      const messageText = workflowInput;
      setWorkflowInput('');
      
      // Scroll to bottom
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
      
      console.log(`💬 [WORKFLOW CHAT] Sending message to session: ${consumerAISession.sessionId}`);
      console.log(`💬 [WORKFLOW CHAT] Message: ${messageText}`);
      
      // Call the workflow chat API
      const response = await axiosInstance.post(`/buyer-profiles/workflow-session/${consumerAISession.sessionId}/chat`, {
        message: messageText,
        leadId: leadId // Include lead ID for proper association
      });
      
      console.log(`✅ [WORKFLOW CHAT] Response received:`, response.data);
      
      if (response.data.success) {
        const aiMessage = {
          sender: 'ai',
          text: response.data.message,
          timestamp: new Date().toLocaleTimeString()
        };
        
        setWorkflowMessages(prev => [...prev, aiMessage]);
        
        // Auto-scroll to bottom
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      } else {
        throw new Error(response.data.error || 'Failed to get AI response');
      }
      
    } catch (error) {
      console.error('❌ [WORKFLOW CHAT] Error sending message:', error);
      
      // Add error message to chat
      const errorMessage = {
        sender: 'ai',
        text: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toLocaleTimeString(),
        isError: true
      };
      
      setWorkflowMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to send message');
    } finally {
      setWorkflowLoading(false);
    }
  };

  const getBuyerStageColor = (stage: string) => {
    switch (stage) {
      case 'researching': return 'secondary';
      case 'pre_approved': return 'primary';
      case 'actively_looking': return 'warning';
      case 'under_contract': return 'success';
      case 'closed': return 'dark';
      default: return 'light';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProgressBarVariant = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 80) return 'primary';
    if (score >= 70) return 'warning';
    return 'danger';
  };

  const getScoreCardClass = (score: number) => {
    if (score >= 90) return 'bg-success';
    if (score >= 80) return 'bg-primary';
    if (score >= 70) return 'bg-warning';
    return 'bg-danger';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Very High';
    if (score >= 70) return 'High';
    if (score >= 60) return 'Good';
    return 'Needs Attention';
  };

  const renderAIInsightsTab = () => ( // eslint-disable-line no-unused-vars
    <div className="ai-insights-container">
      {/* Profile Creation Banner - Show when no profile exists */}
      {!profileExists && (
        <Alert variant="info" className="mb-4 profile-creation-banner">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon={faRobot} className="me-3 fs-4" />
              <div>
                <h5 className="mb-1 fw-bold">🚀 Let AI Help Build This Profile</h5>
                <p className="mb-0">Start an AI conversation to automatically gather buyer preferences, timeline, and requirements. The AI will update the profile as you chat!</p>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setActiveTab('ai-chat')}
              >
                <FontAwesomeIcon icon={faRobot} className="me-1" />
                Start AI Chat
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => setEditing(true)}
              >
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                Manual Entry
              </Button>
            </div>
          </div>
        </Alert>
      )}

      {/* Header Section */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center">
          <div className="neural-icon-wrapper me-3">
            <FontAwesomeIcon icon={faBolt} className="neural-icon" />
          </div>
          <div>
            <h4 className="mb-1 fw-bold">Buyer AI Intelligence Center</h4>
            <p className="text-muted mb-0">Neural network-powered insights for {buyerProfile?.lead_first_name} {buyerProfile?.lead_last_name}</p>
          </div>
        </div>
        <Badge bg={profileExists ? "success" : "warning"} className="ai-status-badge">
          <FontAwesomeIcon icon={profileExists ? faRocket : faExclamationTriangle} className="me-1" />
          {profileExists ? "AI ACTIVE" : "PROFILE NEEDED"}
        </Badge>
      </div>

      {/* Metrics Grid */}
      <Row className="g-4 mb-4">
        <Col md={3}>
          <Card className={`metric-card text-white ${getScoreCardClass(aiMetrics.leadScore)}`}>
            <div className={`trend-indicator trend-${aiMetrics.trends.leadScore.direction}`}>
              <FontAwesomeIcon icon={aiMetrics.trends.leadScore.direction === 'up' ? faArrowTrendUp : faCalendar} className="me-1" />
              {aiMetrics.trends.leadScore.change}
            </div>
            <Card.Body className="text-center metric-card-content">
              <FontAwesomeIcon icon={faChartLine} className="metric-icon mb-2" />
              <h2 className="metric-value mb-1">{aiMetrics.leadScore}</h2>
              <p className="metric-label mb-0">Lead Score</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card text-white bg-primary">
            <div className={`trend-indicator trend-${aiMetrics.trends.daysActive.direction}`}>
              <FontAwesomeIcon icon={faCalendar} className="me-1" />
              {aiMetrics.trends.daysActive.change}
            </div>
            <Card.Body className="text-center metric-card-content">
              <FontAwesomeIcon icon={faCalendar} className="metric-icon mb-2" />
              <h2 className="metric-value mb-1">{aiMetrics.daysActive}</h2>
              <p className="metric-label mb-0">Days Active</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card text-white bg-warning">
            <div className={`trend-indicator trend-${aiMetrics.trends.responseRate.direction}`}>
              <FontAwesomeIcon icon={faArrowTrendUp} className="me-1" />
              {aiMetrics.trends.responseRate.change}
            </div>
            <Card.Body className="text-center metric-card-content">
              <FontAwesomeIcon icon={faEnvelope} className="metric-icon mb-2" />
              <h2 className="metric-value mb-1">{aiMetrics.responseRate}%</h2>
              <p className="metric-label mb-0">Response Rate</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card text-white bg-info">
            <div className={`trend-indicator trend-${aiMetrics.trends.aiConfidence.direction}`}>
              <FontAwesomeIcon icon={faArrowTrendUp} className="me-1" />
              {aiMetrics.trends.aiConfidence.change}
            </div>
            <Card.Body className="text-center metric-card-content">
              <FontAwesomeIcon icon={faBrain} className="metric-icon mb-2" />
              <h2 className="metric-value mb-1">{aiMetrics.aiConfidence}%</h2>
              <p className="metric-label mb-0">AI Confidence</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Priority Alert */}
      <Row className="mb-4">
        <Col md={12}>
          <Alert variant="danger" className="priority-alert">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 fs-4" />
                <div>
                  <h5 className="mb-1 fw-bold">HIGH PRIORITY</h5>
                  <p className="mb-0">Immediate Attention Required - Hot lead ready to buy</p>
                </div>
              </div>
              <Badge bg="light" text="dark" className="px-3 py-2">
                <FontAwesomeIcon icon={faFire} className="me-1" />
                URGENT
              </Badge>
            </div>
          </Alert>
        </Col>
      </Row>

      {/* Intelligence Analysis */}
      <Row className="g-4">
        <Col md={8}>
          <Card className="intelligence-card">
            <Card.Header className="border-bottom-0">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faBrain} className="text-primary me-2" />
                <h5 className="mb-0">AI Buyer Intelligence</h5>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="intelligence-metric mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="metric-title">Buying Intent</span>
                  <Badge bg="success">{getScoreLabel(aiMetrics.buyingIntent)}</Badge>
                </div>
                <ProgressBar 
                  variant={getProgressBarVariant(aiMetrics.buyingIntent)} 
                  now={aiMetrics.buyingIntent} 
                  className="custom-progress-bar"
                />
                <small className="text-muted">Strong indicators: Specific property searches, budget discussions, timeline urgency</small>
              </div>

              <div className="intelligence-metric mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="metric-title">Engagement Quality</span>
                  <Badge bg="primary">{getScoreLabel(aiMetrics.engagementQuality)}</Badge>
                </div>
                <ProgressBar 
                  variant={getProgressBarVariant(aiMetrics.engagementQuality)} 
                  now={aiMetrics.engagementQuality} 
                  className="custom-progress-bar"
                />
                <small className="text-muted">Quick responses, detailed questions, active email engagement</small>
              </div>

              <div className="intelligence-metric">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="metric-title">Conversion Probability</span>
                  <Badge bg="warning">{getScoreLabel(aiMetrics.conversionProbability)}</Badge>
                </div>
                <ProgressBar 
                  variant={getProgressBarVariant(aiMetrics.conversionProbability)} 
                  now={aiMetrics.conversionProbability} 
                  className="custom-progress-bar"
                />
                <small className="text-muted">High potential based on engagement patterns and buying signals</small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="recommendations-card">
            <Card.Header className="border-bottom-0">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faRocket} className="text-warning me-2" />
                <h5 className="mb-0">Recommended Actions</h5>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="action-item urgent mb-3">
                <div className="d-flex align-items-start">
                  <Badge bg="danger" className="action-badge me-3">URGENT</Badge>
                  <div className="flex-grow-1">
                    <h6 className="mb-1">Schedule Property Showing</h6>
                    <p className="text-muted small mb-2">Lead mentioned urgency. Recommend viewing 3 properties this week.</p>
                    <Button size="sm" className="action-btn action-btn-urgent">
                      <FontAwesomeIcon icon={faCalendar} className="me-1" />
                      Schedule Now
                    </Button>
                  </div>
                  <FontAwesomeIcon icon={faHome} className="action-icon text-danger" />
                </div>
              </div>

              <div className="action-item high mb-3">
                <div className="d-flex align-items-start">
                  <Badge bg="warning" className="action-badge me-3">HIGH</Badge>
                  <div className="flex-grow-1">
                    <h6 className="mb-1">Send Pre-Approval Information</h6>
                    <p className="text-muted small mb-2">Buyer ready for next step in purchase process.</p>
                    <Button size="sm" className="action-btn action-btn-high">
                      <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                      Send Info
                    </Button>
                  </div>
                  <FontAwesomeIcon icon={faUserCheck} className="action-icon text-warning" />
                </div>
              </div>

              <div className="action-item medium mb-3">
                <div className="d-flex align-items-start">
                  <Badge bg="info" className="action-badge me-3">MED</Badge>
                  <div className="flex-grow-1">
                    <h6 className="mb-1">Market Analysis Report</h6>
                    <p className="text-muted small mb-2">Provide comparative market analysis for target areas.</p>
                    <Button size="sm" className="action-btn action-btn-medium">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="me-1" />
                      Generate
                    </Button>
                  </div>
                  <FontAwesomeIcon icon={faChartLine} className="action-icon text-info" />
                </div>
              </div>

              {/* Consumer AI Invitation Section */}
              <div className="consumer-ai-section mt-4 pt-3" style={{borderTop: '1px solid #e9ecef'}}>
                <h6 className="mb-3 d-flex align-items-center">
                  <FontAwesomeIcon icon={faRobot} className="text-primary me-2" />
                  Consumer AI Invitations
                </h6>
                
                {!aiEligibility ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary me-2" />
                    <span className="text-muted">Checking AI eligibility...</span>
                  </div>
                ) : aiEligibility.eligible ? (
                  <div className="ai-invitation-options">
                    {aiEligibility.eligibility?.property_matching?.eligible && (
                      <div className="action-item ai-invite mb-2">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faSearch} className="text-success me-2" />
                            <div>
                              <span className="fw-semibold small">Property Matching AI</span>
                              <div className="text-muted" style={{fontSize: '0.7rem'}}>
                                Confidence: {Math.round((aiEligibility.eligibility?.property_matching?.confidence || 0) * 100)}%
                              </div>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline-success"
                            disabled={consumerAILoading || invitationSent}
                            onClick={() => startConsumerAISession('property_matching')}
                          >
                            {consumerAILoading ? (
                              <div className="spinner-border spinner-border-sm" />
                            ) : invitationSent ? (
                              <FontAwesomeIcon icon={faCheckCircle} />
                            ) : (
                              <FontAwesomeIcon icon={faRocket} />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {aiEligibility.eligibility?.price_prediction?.eligible && (
                      <div className="action-item ai-invite mb-2">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faDollarSign} className="text-warning me-2" />
                            <div>
                              <span className="fw-semibold small">Price Prediction AI</span>
                              <div className="text-muted" style={{fontSize: '0.7rem'}}>
                                Confidence: {Math.round((aiEligibility.eligibility?.price_prediction?.confidence || 0) * 100)}%
                              </div>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline-warning"
                            disabled={consumerAILoading || invitationSent}
                            onClick={() => startConsumerAISession('price_prediction')}
                          >
                            {consumerAILoading ? (
                              <div className="spinner-border spinner-border-sm" />
                            ) : invitationSent ? (
                              <FontAwesomeIcon icon={faCheckCircle} />
                            ) : (
                              <FontAwesomeIcon icon={faRocket} />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {aiEligibility.eligibility?.market_analysis?.eligible && (
                      <div className="action-item ai-invite mb-2">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faChartLine} className="text-info me-2" />
                            <div>
                              <span className="fw-semibold small">Market Analysis AI</span>
                              <div className="text-muted" style={{fontSize: '0.7rem'}}>
                                Confidence: {Math.round((aiEligibility.eligibility?.market_analysis?.confidence || 0) * 100)}%
                              </div>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline-info"
                            disabled={consumerAILoading || invitationSent}
                            onClick={() => startConsumerAISession('market_analysis')}
                          >
                            {consumerAILoading ? (
                              <div className="spinner-border spinner-border-sm" />
                            ) : invitationSent ? (
                              <FontAwesomeIcon icon={faCheckCircle} />
                            ) : (
                              <FontAwesomeIcon icon={faRocket} />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {invitationSent && aiInvitation && (
                      <Alert variant="success" className="mt-3 mb-0">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          <div>
                            <strong>AI Invitation Sent!</strong>
                            <div className="small mt-1">
                              {aiInvitation.invitation_text}
                            </div>
                            {consumerAISession && (
                              <div className="small text-muted mt-1">
                                Session ID: {consumerAISession.request_id}
                              </div>
                            )}
                          </div>
                        </div>
                      </Alert>
                    )}
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    This buyer is not yet eligible for Consumer AI. Continue building engagement to unlock AI features.
                  </Alert>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderProfileTab = () => ( // eslint-disable-line no-unused-vars
    <Row>
      <Col md={6}>
        <Card className="mb-4" style={{ 
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
              <strong>Basic Information</strong>
            </div>
            {!editing ? (
              <Button size="sm" variant="outline-primary" onClick={() => setEditing(true)}>
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                Edit
              </Button>
            ) : (
              <div>
                <Button size="sm" variant="success" className="me-2" onClick={saveBuyerProfile} disabled={saving}>
                  <FontAwesomeIcon icon={faSave} className="me-1" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            )}
          </Card.Header>
          <Card.Body>
            {buyerProfile && (
              <div className="space-y-3">
                <div className="row mb-3">
                  <div className="col-sm-6">
                    <label className="form-label">First Name</label>
                    {editing ? (
                      <Form.Control
                        value={formData.lead_first_name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, lead_first_name: e.target.value }))}
                      />
                    ) : (
                      <div className="form-control-plaintext">{buyerProfile.lead_first_name}</div>
                    )}
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">Last Name</label>
                    {editing ? (
                      <Form.Control
                        value={formData.lead_last_name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, lead_last_name: e.target.value }))}
                      />
                    ) : (
                      <div className="form-control-plaintext">{buyerProfile.lead_last_name}</div>
                    )}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-sm-6">
                    <label className="form-label">
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                      Email
                    </label>
                    {editing ? (
                      <Form.Control
                        type="email"
                        value={formData.lead_email || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, lead_email: e.target.value }))}
                      />
                    ) : (
                      <div className="form-control-plaintext">{buyerProfile.lead_email}</div>
                    )}
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">
                      <FontAwesomeIcon icon={faPhone} className="me-1" />
                      Phone
                    </label>
                    {editing ? (
                      <Form.Control
                        value={formData.lead_phone || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, lead_phone: e.target.value }))}
                      />
                    ) : (
                      <div className="form-control-plaintext">{buyerProfile.lead_phone}</div>
                    )}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-sm-6">
                    <label className="form-label">Buyer Stage</label>
                    {editing ? (
                      <Form.Select
                        value={formData.buyer_stage || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, buyer_stage: e.target.value }))}
                      >
                        <option value="researching">Researching</option>
                        <option value="viewing">Viewing Properties</option>
                        <option value="offering">Making Offers</option>
                        <option value="under_contract">Under Contract</option>
                        <option value="closed">Closed</option>
                        <option value="paused">Paused</option>
                        <option value="lost">Lost</option>
                      </Form.Select>
                    ) : (
                      <div>
                        <Badge bg={getBuyerStageColor(buyerProfile.buyer_stage)}>
                          {buyerProfile.buyer_stage.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">Buying Timeframe</label>
                    {editing ? (
                      <Form.Select
                        value={formData.buying_timeframe || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, buying_timeframe: e.target.value }))}
                      >
                        <option value="within_3_months">0-3 months</option>
                        <option value="3_to_6_months">3-6 months</option>
                        <option value="6_to_12_months">6-12 months</option>
                        <option value="over_12_months">12+ months</option>
                        <option value="not_specified">Not specified</option>
                      </Form.Select>
                    ) : (
                      <div className="form-control-plaintext">
                        {buyerProfile.buying_timeframe?.replace('_', ' ') || 'Not specified'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-sm-6">
                    <label className="form-label">Pre-Approved</label>
                    {editing ? (
                      <Form.Check
                        type="checkbox"
                        checked={formData.pre_approved || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, pre_approved: e.target.checked }))}
                        label="Pre-approved for financing"
                      />
                    ) : (
                      <div className="form-control-plaintext">
                        <Badge bg={buyerProfile.pre_approved ? 'success' : 'secondary'}>
                          {buyerProfile.pre_approved ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">Pre-Approval Amount</label>
                    {editing ? (
                      <Form.Control
                        type="number"
                        value={formData.pre_approval_amount || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, pre_approval_amount: parseFloat(e.target.value) }))}
                        placeholder="Enter pre-approval amount"
                      />
                    ) : (
                      <div className="form-control-plaintext">
                        {buyerProfile.pre_approval_amount ? formatCurrency(Number(buyerProfile.pre_approval_amount)) : 'Not specified'}
                      </div>
                    )}
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">
                      <FontAwesomeIcon icon={faCalendar} className="me-1" />
                      Timeline
                    </label>
                    {editing ? (
                      <Form.Select
                        value={formData.buying_timeframe || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, buying_timeframe: e.target.value }))}
                      >
                        <option value="within_30_days">Within 30 Days</option>
                        <option value="within_3_months">Within 3 Months</option>
                        <option value="within_6_months">Within 6 Months</option>
                        <option value="within_1_year">Within 1 Year</option>
                        <option value="just_looking">Just Looking</option>
                      </Form.Select>
                    ) : (
                      <div className="form-control-plaintext">
                        {buyerProfile.buying_timeframe.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    )}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-sm-6">
                    <label className="form-label">Pre-Approved</label>
                    {editing ? (
                      <Form.Check
                        type="checkbox"
                        checked={formData.pre_approved || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, pre_approved: e.target.checked }))}
                        label="Yes, pre-approved"
                      />
                    ) : (
                      <div>
                        {buyerProfile.pre_approved ? (
                          <Badge bg="success">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                            Pre-Approved
                          </Badge>
                        ) : (
                          <Badge bg="warning">
                            <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                            Not Pre-Approved
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-sm-6">
                    {buyerProfile.pre_approved && (
                      <>
                        <label className="form-label">Pre-Approval Amount</label>
                        {editing ? (
                          <Form.Control
                            type="number"
                            value={formData.pre_approval_amount || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, pre_approval_amount: parseInt(e.target.value) }))}
                          />
                        ) : (
                          <div className="form-control-plaintext">
                            {formatCurrency(buyerProfile.pre_approval_amount || 0)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={6}>
        <Card className="mb-4" style={{ 
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Card.Header>
            <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
            <strong>Budget Information</strong>
          </Card.Header>
          <Card.Body>
            {buyerProfile && (
              <div>
                <div className="row mb-3">
                  <div className="col-sm-6">
                    <label className="form-label">Budget Minimum</label>
                    {editing ? (
                      <Form.Control
                        type="number"
                        value={formData.budget_min || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, budget_min: parseInt(e.target.value) }))}
                      />
                    ) : (
                      <div className="form-control-plaintext">{buyerProfile.budget_min ? formatCurrency(buyerProfile.budget_min) : 'Not specified'}</div>
                    )}
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">Budget Maximum</label>
                    {editing ? (
                      <Form.Control
                        type="number"
                        value={formData.budget_max || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, budget_max: parseInt(e.target.value) }))}
                      />
                    ) : (
                      <div className="form-control-plaintext">{buyerProfile.budget_max ? formatCurrency(buyerProfile.budget_max) : 'Not specified'}</div>
                    )}
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Budget Range</label>
                  <div className="d-flex align-items-center">
                    <ProgressBar 
                      className="flex-grow-1 me-3" 
                      style={{ height: '8px' }}
                      now={100} 
                      variant="success"
                    />
                    <span className="text-muted small">
                      {buyerProfile.budget_min && buyerProfile.budget_max 
                        ? `${formatCurrency(buyerProfile.budget_min)} - ${formatCurrency(buyerProfile.budget_max)}`
                        : 'Budget not specified'
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card style={{ 
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 212, 255, 0.2)'
        }}>
          <Card.Header>
            <FontAwesomeIcon icon={faRobot} className="me-2 text-info" />
            <strong>AI Engagement Status</strong>
          </Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span>Consumer AI Eligible</span>
              <Badge bg="success">
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Ready
              </Badge>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span>AI Interactions</span>
              <Badge bg="primary">{aiInteractions.length}</Badge>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span>Last AI Session</span>
              <span className="text-muted small">
                {aiInteractions.length > 0 ? 'Recently active' : 'No sessions yet'}
              </span>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderPreferencesTab = () => ( // eslint-disable-line no-unused-vars
    <Row>
      <Col md={4}>
        <Card className="mb-4" style={{ 
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Card.Header>
            <FontAwesomeIcon icon={faHome} className="me-2 text-warning" />
            <strong>Property Preferences</strong>
          </Card.Header>
          <Card.Body>
            {buyerProfile?.property_preferences && (
              <div className="space-y-2">
                <div className="d-flex justify-content-between">
                  <span>Property Type:</span>
                  <span className="fw-semibold">{buyerProfile.property_preferences.property_type || 'Not specified'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Bedrooms:</span>
                  <span className="fw-semibold">{buyerProfile.property_preferences.bedrooms || 'Any'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Bathrooms:</span>
                  <span className="fw-semibold">{buyerProfile.property_preferences.bathrooms || 'Any'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Square Feet:</span>
                  <span className="fw-semibold">
                    {buyerProfile.property_preferences.square_feet_min || 0} - {buyerProfile.property_preferences.square_feet_max || 'No max'}
                  </span>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={4}>
        <Card className="mb-4" style={{ 
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Card.Header>
            <FontAwesomeIcon icon={faMapMarker} className="me-2 text-danger" />
            <strong>Location Preferences</strong>
          </Card.Header>
          <Card.Body>
            {buyerProfile?.location_preferences && (
              <div className="space-y-2">
                <div>
                  <span className="text-muted">Preferred Cities:</span>
                  <div className="mt-1">
                    {buyerProfile.location_preferences.cities?.length > 0 ? (
                      buyerProfile.location_preferences.cities.map((city: string, index: number) => (
                        <Badge key={index} bg="primary" className="me-1 mb-1">{city}</Badge>
                      ))
                    ) : (
                      <span className="text-muted small">No cities specified</span>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-muted">Neighborhoods:</span>
                  <div className="mt-1">
                    {buyerProfile.location_preferences.neighborhoods?.length > 0 ? (
                      buyerProfile.location_preferences.neighborhoods.map((neighborhood: string, index: number) => (
                        <Badge key={index} bg="secondary" className="me-1 mb-1">{neighborhood}</Badge>
                      ))
                    ) : (
                      <span className="text-muted small">No neighborhoods specified</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={4}>
        <Card style={{ 
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Card.Header>
            <FontAwesomeIcon icon={faHeart} className="me-2 text-success" />
            <strong>Lifestyle Preferences</strong>
          </Card.Header>
          <Card.Body>
            {buyerProfile?.lifestyle_preferences && (
              <div className="space-y-3">
                <div>
                  <span className="text-muted small">Must Have:</span>
                  <div className="mt-1">
                    {buyerProfile.lifestyle_preferences.must_have?.length > 0 ? (
                      buyerProfile.lifestyle_preferences.must_have.map((item: string, index: number) => (
                        <Badge key={index} bg="success" className="me-1 mb-1">{item}</Badge>
                      ))
                    ) : (
                      <span className="text-muted small">No requirements specified</span>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-muted small">Nice to Have:</span>
                  <div className="mt-1">
                    {buyerProfile.lifestyle_preferences.nice_to_have?.length > 0 ? (
                      buyerProfile.lifestyle_preferences.nice_to_have.map((item: string, index: number) => (
                        <Badge key={index} bg="info" className="me-1 mb-1">{item}</Badge>
                      ))
                    ) : (
                      <span className="text-muted small">No preferences specified</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderActivityTab = () => (
    <Row>
      <Col md={6}>
        <Card className="mb-4" style={{ 
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 212, 255, 0.2)'
        }}>
          <Card.Header>
            <FontAwesomeIcon icon={faBrain} className="me-2 text-info" />
            <strong>AI Interactions</strong>
          </Card.Header>
          <Card.Body>
            {aiInteractions.length > 0 ? (
              <div className="space-y-3">
                {aiInteractions.slice(0, 5).map((interaction) => (
                  <div key={interaction.id} className="border-bottom pb-2 mb-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <Badge bg="primary" className="me-2">
                          {interaction.interaction_type.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <span className="small text-muted">
                          {new Date(interaction.created_on).toLocaleDateString()}
                        </span>
                      </div>
                      <FontAwesomeIcon icon={faEye} className="text-muted" />
                    </div>
                    {interaction.ai_insights && (
                      <div className="mt-2 small text-muted">
                        {Object.keys(interaction.ai_insights).length} insights generated
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                <FontAwesomeIcon icon={faRobot} size="2x" className="mb-2" />
                <div>No AI interactions yet</div>
                <small>Consumer AI sessions will appear here</small>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={6}>
        <Card style={{ 
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Card.Header>
            <FontAwesomeIcon icon={faSearch} className="me-2 text-warning" />
            <strong>Search History</strong>
          </Card.Header>
          <Card.Body>
            {searchHistory.length > 0 ? (
              <div className="space-y-3">
                {searchHistory.slice(0, 5).map((search) => (
                  <div key={search.id} className="border-bottom pb-2 mb-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="small">
                        <FontAwesomeIcon icon={faSearch} className="me-1 text-muted" />
                        {search.results_count} results found
                      </div>
                      <span className="small text-muted">
                        {new Date(search.created_on).toLocaleDateString()}
                      </span>
                    </div>
                    {search.search_criteria && (
                      <div className="mt-1 small text-muted">
                        Search criteria: {Object.keys(search.search_criteria).length} filters
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                <FontAwesomeIcon icon={faSearch} size="2x" className="mb-2" />
                <div>No search history</div>
                <small>Property searches will appear here</small>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // Add performance metrics display for user feedback
  const renderPerformanceMetrics = () => { // eslint-disable-line no-unused-vars
    // This would show performance info for dev/admin users
    return null; // Hidden in production
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading buyer profile...</div>
        </div>
      </div>
    );
  }



  return (
    <div className="buyer-profile-page">
      <style>{`
        .buyer-profile-page {
          background: #f8f9fa;
          min-height: 100vh;
        }

        .main-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.05);
          margin: 20px;
          overflow: hidden;
        }

        .compact-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px 30px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .neural-icon-wrapper {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3);
        }

        .neural-icon {
          font-size: 24px;
          color: white;
        }

        .ai-status-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
        }

        .metric-card {
          border: none;
          border-radius: 16px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .metric-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
        }

        .metric-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s;
        }

        .metric-card:hover::before {
          left: 100%;
        }

        .metric-icon {
          font-size: 2rem;
          opacity: 0.9;
        }

        .metric-value {
          font-size: 2.5rem;
          font-weight: 700;
          margin: 0;
        }

        .metric-label {
          font-size: 0.9rem;
          opacity: 0.9;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .priority-alert {
          border: none;
          border-radius: 16px;
          box-shadow: 0 8px 25px rgba(220, 53, 69, 0.3);
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          color: white;
        }

        .intelligence-card, .recommendations-card {
          border: none;
          border-radius: 16px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
        }

        .intelligence-metric {
          padding: 15px 0;
        }

        .metric-title {
          font-weight: 600;
          font-size: 1rem;
          color: #2d3748;
        }

        .custom-progress-bar {
          height: 8px;
          border-radius: 10px;
          background-color: rgba(0, 0, 0, 0.1);
        }

        .custom-progress-bar .progress-bar {
          border-radius: 10px;
        }

        .action-item {
          padding: 16px;
          border-radius: 12px;
          background: rgba(248, 249, 250, 0.8);
          border: 1px solid rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .action-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .action-badge {
          min-width: 60px;
          text-align: center;
          font-weight: 600;
          font-size: 0.75rem;
          padding: 4px 8px;
        }

        .action-icon {
          font-size: 1.5rem;
          opacity: 0.7;
        }

        .action-btn {
          border-radius: 8px;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.8rem;
          letter-spacing: 0.5px;
          padding: 8px 16px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          position: relative;
          overflow: hidden;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
        }

        .action-btn:active {
          transform: translateY(0);
          transition: transform 0.1s;
        }

        .action-btn-urgent {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          border: none;
          color: white;
        }

        .action-btn-high {
          background: linear-gradient(135deg, #fd7e14 0%, #e55a00 100%);
          border: none;
          color: white;
        }

        .action-btn-medium {
          background: linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%);
          border: none;
          color: white;
        }

        .ai-insights-container {
          animation: fadeInUp 0.6s ease;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .nav-tabs {
          border-bottom: 1px solid #e9ecef;
          background: #f8f9fa;
          padding: 0 30px;
          margin: 0;
        }

        .nav-tabs .nav-link {
          border: none;
          border-radius: 8px 8px 0 0;
          font-weight: 600;
          color: #6c757d;
          background: transparent;
          margin-right: 5px;
          margin-bottom: -1px;
          transition: all 0.3s ease;
          text-transform: uppercase;
          font-size: 0.85rem;
          letter-spacing: 0.5px;
        }

        .nav-tabs .nav-link.active {
          background: white;
          color: #495057;
          border-bottom: 2px solid #667eea;
        }

        .tab-content {
          background: white;
          padding: 30px;
          border-radius: 0 0 16px 16px;
        }

        .consumer-ai-banner {
          background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
          border: none;
          border-radius: 0;
          color: white;
          padding: 12px 30px;
          margin: 0;
        }

        .compact-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0;
        }

        .compact-subtitle {
          font-size: 0.9rem;
          opacity: 0.9;
          margin: 0;
        }

        .trend-indicator {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .trend-up {
          background: rgba(40, 167, 69, 0.2);
          color: #28a745;
        }

        .trend-down {
          background: rgba(220, 53, 69, 0.2);
          color: #dc3545;
        }

        .trend-neutral {
          background: rgba(108, 117, 125, 0.2);
          color: #6c757d;
        }

        .metric-card-content {
          position: relative;
          z-index: 2;
        }

        .consumer-ai-section {
          background: rgba(248, 249, 250, 0.8);
          border-radius: 12px;
          padding: 20px;
          margin-top: 20px;
        }

        .ai-invitation-options .action-item.ai-invite {
          background: white;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 8px;
          padding: 12px;
          transition: all 0.3s ease;
        }

        .ai-invitation-options .action-item.ai-invite:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          border-color: rgba(13, 110, 253, 0.3);
        }

        .consumer-ai-session .session-details {
          animation: fadeInUp 0.6s ease;
        }

        .invitation-preview {
          position: relative;
          overflow: hidden;
        }

        .invitation-preview::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          transition: left 0.8s;
        }

        .invitation-preview:hover::before {
          left: 100%;
        }

        .analytics-item {
          padding: 8px 0;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .analytics-item:last-child {
          border-bottom: none;
        }
      `}</style>

      <div className="main-card">
        {/* Compact Header */}
        <div className="compact-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="compact-title text-white">
                <FontAwesomeIcon icon={faBolt} className="me-2" style={{color: '#00d4ff'}} />
                Lead AI Assistant
              </h1>
              <p className="compact-subtitle text-white mb-0">
                Neural network-powered insights for {buyerProfile?.lead_first_name} {buyerProfile?.lead_last_name}
              </p>
            </div>
            <Badge bg="success" className="ai-status-badge">
              <FontAwesomeIcon icon={faRocket} className="me-1" />
              AI ACTIVE
            </Badge>
          </div>
        </div>

        {/* Consumer AI Banner */}
        <Alert variant="info" className="consumer-ai-banner">
          <FontAwesomeIcon icon={faRobot} className="me-2" />
          <strong>Consumer AI Ready:</strong> This buyer is eligible for AI-powered property matching and market analysis.
        </Alert>

        {/* Main Content Tabs */}
        <Tabs 
          activeKey={activeTab} 
          onSelect={(k) => setActiveTab(k || 'ai-chat')}
          className="buyer-profile-tabs"
        >
          <Tab 
            eventKey="ai-chat" 
            title={
              <span>
                <FontAwesomeIcon icon={faRobot} className="me-1" />
                AI CHAT
                {consumerAISession && (
                  <Badge bg="success" className="ms-2">ACTIVE</Badge>
                )}
              </span>
            }
          >
            {consumerAISession ? (
              // Active Workflow Session Chat Interface
              <div className="workflow-chat-interface">
                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <FontAwesomeIcon icon={faRobot} className="text-primary me-2" />
                      <strong>{consumerAISession.session_data?.stageName || 'Profile Building Session'}</strong>
                    </div>
                    <Badge bg="success">Active</Badge>
                  </Card.Header>
                  <Card.Body>
                    <div className="session-info mb-3">
                      <Row>
                        <Col md={6}>
                          <small className="text-muted">Session Type:</small>
                          <div className="fw-semibold">{consumerAISession.ai_type?.replace('_', ' ').toUpperCase()}</div>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">Estimated Duration:</small>
                          <div className="fw-semibold">{consumerAISession.session_data?.estimatedDuration || '5-10 minutes'}</div>
                        </Col>
                      </Row>
                    </div>
                    
                    <div className="chat-container" style={{ height: '400px', border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '1rem', overflowY: 'auto', backgroundColor: '#f8f9fa' }} ref={chatContainerRef}>
                      {/* Initial AI Message */}
                      <div className="chat-message mb-3">
                        <div className="d-flex align-items-start">
                          <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-3">
                            <FontAwesomeIcon icon={faRobot} className="text-white" />
                          </div>
                          <div className="flex-grow-1">
                            <div className="bg-white p-3 rounded shadow-sm">
                              <div className="fw-semibold mb-1">AI Assistant</div>
                              <div>{consumerAISession.session_data?.initialMessage || "Hello! I&apos;m here to help you build a comprehensive buyer profile. Let&apos;s start gathering information about your client."}</div>
                            </div>
                            <small className="text-muted">Just now</small>
                          </div>
                        </div>
                      </div>
                      
                      {/* Chat Messages */}
                      {workflowMessages.map((message, index) => (
                        <div key={index} className={`chat-message mb-3 ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}>
                          <div className={`d-flex align-items-start ${message.sender === 'user' ? 'justify-content-end' : ''}`}>
                            {message.sender === 'ai' && (
                              <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-3">
                                <FontAwesomeIcon icon={faRobot} className="text-white" />
                              </div>
                            )}
                            <div className={`flex-grow-1 ${message.sender === 'user' ? 'text-end' : ''}`} style={{ maxWidth: '85%' }}>
                              <div className={`p-3 rounded shadow-sm ${message.sender === 'user' ? 'bg-primary text-white ms-auto' : 'bg-white'}`}>
                                {message.sender === 'ai' && <div className="fw-semibold mb-1">AI Assistant</div>}
                                <div>{message.text}</div>
                              </div>
                              <small className="text-muted">{message.timestamp}</small>
                            </div>
                            {message.sender === 'user' && (
                              <div className="avatar-sm bg-secondary rounded-circle d-flex align-items-center justify-content-center ms-3">
                                <FontAwesomeIcon icon={faUser} className="text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Loading indicator */}
                      {workflowLoading && (
                        <div className="chat-message mb-3">
                          <div className="d-flex align-items-start">
                            <div className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-3">
                              <FontAwesomeIcon icon={faRobot} className="text-white" />
                            </div>
                            <div className="flex-grow-1">
                              <div className="bg-white p-3 rounded shadow-sm">
                                <div className="d-flex align-items-center">
                                  <div className="spinner-border spinner-border-sm text-primary me-2" />
                                  <span className="text-muted">AI is thinking...</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="chat-input mt-3">
                      <div className="input-group">
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="Type your response about the client..."
                          value={workflowInput}
                          onChange={(e) => setWorkflowInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && !workflowLoading && sendWorkflowMessage()}
                          disabled={workflowLoading}
                        />
                        <Button 
                          variant="primary" 
                          onClick={sendWorkflowMessage}
                          disabled={workflowLoading || !workflowInput.trim()}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </Button>
                      </div>
                      <small className="text-muted">
                        {workflowLoading ? 'Sending message...' : 'Press Enter to send or click the send button'}
                      </small>
                      
                      {/* Debug info */}
                      <div className="mt-2 p-2 bg-light rounded small">
                        <strong>Debug Info:</strong><br/>
                        Session ID: {consumerAISession?.sessionId || 'Not set'}<br/>
                        Input: {workflowInput || 'Empty'}<br/>
                        Loading: {workflowLoading ? 'Yes' : 'No'}<br/>
                        <Button 
                          size="sm" 
                          variant="outline-secondary" 
                          onClick={() => {
                            console.log('🔧 [DEBUG] Manual function call');
                            sendWorkflowMessage();
                          }}
                        >
                          Debug Send
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                
                <Card>
                  <Card.Header>
                    <FontAwesomeIcon icon={faListCheck} className="text-info me-2" />
                    <strong>Information to Collect</strong>
                  </Card.Header>
                  <Card.Body>
                    <div className="fields-checklist">
                      {consumerAISession.session_data?.fieldsToCollect?.map((field: string, index: number) => (
                        <div key={index} className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon icon={faSquare} className="text-muted me-2" />
                          <span className="text-capitalize">{field.replace('_', ' ')}</span>
                        </div>
                      )) || (
                        <div className="text-muted">Field collection list will appear here</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ) : (
              <div>
                {!profileExists ? (
                  // Profile Creation Workflow - Show this as the first option when coming from AI Assistant
                  <div className="profile-creation-workflow">
                    <Alert variant="primary" className="mb-4">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faRobot} className="me-3 fs-4" />
                        <div>
                          <h5 className="mb-1 fw-bold">🤖 AI-Powered Profile Builder</h5>
                          <p className="mb-0">Let our AI assistant gather buyer information through natural conversation. The profile will be built automatically as you chat!</p>
                        </div>
                      </div>
                    </Alert>

                    <Row>
                      <Col md={8}>
                        <Card className="mb-4">
                          <Card.Header>
                            <FontAwesomeIcon icon={faRobot} className="text-primary me-2" />
                            <strong>Start Profile Building Session</strong>
                          </Card.Header>
                          <Card.Body>
                            <div className="workflow-options">
                              <div className="option-item mb-3 p-3 border rounded">
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <h6 className="mb-1">
                                      <FontAwesomeIcon icon={faSearch} className="text-primary me-2" />
                                      Property Preferences Discovery
                                    </h6>
                                    <p className="text-muted mb-0 small">AI will ask about property type, size, location preferences, and must-have features</p>
                                  </div>
                                  <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={() => startConsumerAISession('property_discovery')}
                                    disabled={consumerAILoading}
                                  >
                                    {consumerAILoading ? <Spinner size="sm" /> : 'Start Chat'}
                                  </Button>
                                </div>
                              </div>

                              <div className="option-item mb-3 p-3 border rounded">
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <h6 className="mb-1">
                                      <FontAwesomeIcon icon={faDollarSign} className="text-success me-2" />
                                      Budget & Financing Discussion
                                    </h6>
                                    <p className="text-muted mb-0 small">Gather budget range, pre-approval status, and financing preferences</p>
                                  </div>
                                  <Button 
                                    variant="success" 
                                    size="sm"
                                    onClick={() => startConsumerAISession('budget_discovery')}
                                    disabled={consumerAILoading}
                                  >
                                    {consumerAILoading ? <Spinner size="sm" /> : 'Start Chat'}
                                  </Button>
                                </div>
                              </div>

                              <div className="option-item mb-3 p-3 border rounded">
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <h6 className="mb-1">
                                      <FontAwesomeIcon icon={faClock} className="text-warning me-2" />
                                      Timeline & Motivation
                                    </h6>
                                    <p className="text-muted mb-0 small">Understand buying timeline, motivation, and urgency level</p>
                                  </div>
                                  <Button 
                                    variant="warning" 
                                    size="sm"
                                    onClick={() => startConsumerAISession('timeline_discovery')}
                                    disabled={consumerAILoading}
                                  >
                                    {consumerAILoading ? <Spinner size="sm" /> : 'Start Chat'}
                                  </Button>
                                </div>
                              </div>

                              <div className="option-item mb-3 p-3 border rounded bg-light">
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <h6 className="mb-1">
                                      <FontAwesomeIcon icon={faCheckCircle} className="text-info me-2" />
                                      Complete Profile Building
                                    </h6>
                                    <p className="text-muted mb-0 small">Comprehensive session covering all aspects of buyer profile</p>
                                  </div>
                                  <Button 
                                    variant="info" 
                                    size="sm"
                                    onClick={() => startConsumerAISession('complete_profile')}
                                    disabled={consumerAILoading}
                                  >
                                    {consumerAILoading ? <Spinner size="sm" /> : 'Start Chat'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>

                      <Col md={4}>
                        <Card>
                          <Card.Header>
                            <FontAwesomeIcon icon={faQuestionCircle} className="text-info me-2" />
                            <strong>How It Works</strong>
                          </Card.Header>
                          <Card.Body>
                            <div className="step-item mb-3">
                              <div className="d-flex align-items-start">
                                <Badge bg="primary" className="me-3">1</Badge>
                                <div>
                                  <h6 className="mb-1">Choose Topic</h6>
                                  <p className="text-muted small mb-0">Select what aspect of the buyer profile to focus on</p>
                                </div>
                              </div>
                            </div>

                            <div className="step-item mb-3">
                              <div className="d-flex align-items-start">
                                <Badge bg="primary" className="me-3">2</Badge>
                                <div>
                                  <h6 className="mb-1">AI Conversation</h6>
                                  <p className="text-muted small mb-0">Natural chat with AI to gather information</p>
                                </div>
                              </div>
                            </div>

                            <div className="step-item mb-3">
                              <div className="d-flex align-items-start">
                                <Badge bg="primary" className="me-3">3</Badge>
                                <div>
                                  <h6 className="mb-1">Auto-Update</h6>
                                  <p className="text-muted small mb-0">Profile fields are automatically populated</p>
                                </div>
                              </div>
                            </div>

                            <div className="step-item">
                              <div className="d-flex align-items-start">
                                <Badge bg="success" className="me-3">✓</Badge>
                                <div>
                                  <h6 className="mb-1">Complete Profile</h6>
                                  <p className="text-muted small mb-0">Ready for property matching and analysis</p>
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                ) : (
                  // Existing profile - show chat interface for updates
                  <div className="existing-profile-chat">
                    <Alert variant="success" className="mb-4">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-3 fs-4" />
                        <div>
                          <h5 className="mb-1 fw-bold">✅ Profile Complete</h5>
                          <p className="mb-0">This buyer profile is ready! You can still chat with AI to update or refine any information.</p>
                        </div>
                      </div>
                    </Alert>
                    
                    <Card>
                      <Card.Header>
                        <FontAwesomeIcon icon={faRobot} className="text-primary me-2" />
                        <strong>Update Profile Information</strong>
                      </Card.Header>
                      <Card.Body>
                        <p className="text-muted mb-3">Start a conversation to update any aspect of this buyer&apos;s profile.</p>
                        <div className="d-flex gap-2">
                          <Button 
                            variant="primary" 
                            onClick={() => startConsumerAISession('property_discovery')}
                            disabled={consumerAILoading}
                          >
                            <FontAwesomeIcon icon={faHome} className="me-1" />
                            Update Preferences
                          </Button>
                          <Button 
                            variant="success" 
                            onClick={() => startConsumerAISession('budget_discovery')}
                            disabled={consumerAILoading}
                          >
                            <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                            Update Budget
                          </Button>
                          <Button 
                            variant="warning" 
                            onClick={() => startConsumerAISession('timeline_discovery')}
                            disabled={consumerAILoading}
                          >
                            <FontAwesomeIcon icon={faClock} className="me-1" />
                            Update Timeline
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                )}
              </div>
            )}
          </Tab>
          
          <Tab 
            eventKey="history" 
            title={
              <span>
                <FontAwesomeIcon icon={faHistory} className="me-1" />
                HISTORY
              </span>
            }
          >
            {renderActivityTab()}
          </Tab>
        </Tabs>
      </div>
    </div>
  );
};

export default BuyerProfilePage; 